/**
 * PatientsTab.tsx — the PMS patient directory (UX Wave 6 §5 — the
 * HubSpot-contacts equivalent), mounted by BOTH staff consoles.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 6 §5 (PMS).
 *
 * WHY: a searchable, paginated directory of ALL patients over the identity
 * read GET /v1/identity/admin/patients (ADMIN **and** CLINICIAN — the
 * identical money-free shape). The search box debounces into the server-side
 * `q` (email/name contains); pagination is CURSOR-based (`before` = the last
 * visible row's createdAt) but presented through the SHARED ui/Pagination
 * control: fetched pages are cached per query, Previous re-shows a cached
 * page without a refetch, and Next fetches the next cursor page only when it
 * isn't cached yet. Because a cursor read never knows the total, the control
 * is fed `known rows + one more page while the end hasn't been seen` — the
 * page count grows as the admin pages until the short (or empty) page marks
 * the end. A row click opens the patient RECORD drawer (PatientRecordDrawer)
 * and the OPEN PATIENT LIVES IN THE URL (`?patient=<id>`) so the record is
 * deep-linkable both ways — the Payments drawer's "View patient record" link
 * lands here (`?tab=patients&patient=<id>`), and the record's subscription
 * rows link back to `?tab=orders&subscription=<id>`.
 *
 * VARIANTS: `variant='admin'` (the /admin console) gets the full record —
 * including the ADMIN-only "Subscriptions & payments" tab; `variant='clinician'`
 * is MONEY-FREE BY CONSTRUCTION — the drawer never offers that tab and the
 * orders patient-context read is NEVER requested (the money-redaction sweep
 * renders this surface as a CLINICIAN and asserts both). The identity
 * directory itself carries no money field at all.
 *
 * Deep-link resolution: a `?patient=` id that is not on the loaded pages
 * (e.g. a link into a later page) falls back to the batched identity lookup —
 * ADMIN-only, so the clinician variant skips the fallback rather than fire a
 * request the gateway would 403 (the row-click path always has the row).
 *
 * TEST RECORDS (identity #86, 2026-09-04). The CTO opened this screen and found
 * `test400@mailinator.com` and `test100@mailinator.com`, both named "test test",
 * sitting between real patients. identity excludes flagged rows from the default
 * read — as a WHERE term, before `take`, so a flagged row cannot reach the page
 * at all — and this screen carries the two surfaces that make the flag usable:
 *
 *  - **"Show test records"**, now a facet of the FILTER PANEL rather than an
 *    inline checkbox (`PatientsFilters.tsx`, 2026-09-05) — the same label,
 *    verbatim, and now the same SHAPE as the pipeline's: a "Filters (n)" button,
 *    a modal, and the applied state as a removable chip. It sends
 *    `includeTest=true` — the literal string, see `AdminPatientListQuery` — and
 *    is OMITTED for the normal read.
 *  - **the muted TEST chip + the struck-through name**, exactly as the pipeline
 *    card renders them (`pipeline/PipelineBoard.tsx`). Muted on purpose: staff
 *    must be able to spot a rehearsal record at a glance without it shouting
 *    over the clinical cues beside it.
 *
 * ⚠️ THERE IS NO MARK ACTION ANYWHERE ON THIS SCREEN (2026-09-05) — not on a
 * directory row, and not in the record drawer either. AND THAT IS THE POINT.
 * A test record could be marked in THREE independent places — the deal, the
 * money board and a per-row control here — and the three routinely disagreed,
 * leaving half-marked records that stayed visible wherever they had not been
 * marked. The ROUTINE path is the DEAL: clinic-service's cascade marks the
 * deal, its plan and its patient in one action.
 *
 * A brief second attempt is worth knowing about, so nobody makes it a third
 * time: a drawer mark gated on "this patient has no deals" was added and
 * removed the same day. This screen has no per-patient deal read — the drawer
 * infers deals from the BOARD's lane-paged list (`perLane=25`) filtered
 * client-side by email — so "no deals" is indistinguishable from "their deal
 * is older than the 25th in its lane", which is the steady state on a busy
 * lane, not an edge case. The gate would have offered the mark over
 * established patients. See `TestRecordAction` in `PatientRecordDrawer.tsx`
 * for the full write-up and for the server read that would close the gap.
 *
 * ⚠️ WHAT DOES SURVIVE HERE IS THE UN-MARK: `PatientRecordDrawer`'s Overview
 * tab, ADMIN only, unconditional, and reading no deal data at all. THE
 * CASCADE IS ON `dev` NOW (verified 2026-09-05: clinic `dev` = 9fb728c,
 * orders `dev` = 46784b3, identity `dev` = fdc9d21), so the reason it was
 * first kept — "nothing else can clear this flag yet" — has expired. It stays
 * because the cascade WITHHOLDS the human leg by design: it moves
 * `User.isTest` only when every other deal of that patient agrees with where
 * it is going, in BOTH directions, so a real patient cannot be hidden by one
 * rehearsal journey and cannot be un-hidden while their other journeys are
 * still test records. That cohort, and any patient with no deal at all, is
 * un-flaggable only from here. See `TestRecordAction` for the full argument.
 *
 * What is unconditional here is the READ — the chip and the strikethrough —
 * because showing that a record IS a test record is a different thing from
 * offering to make it one, and staff in the audit view still have to tell the
 * fictional rows from the real ones.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listPatients, lookupStaffContactMap } from '../api/identity';
import type { AdminPatientRow, PatientTreatmentStatus } from '../api/types';
import { getPatientTreatmentStatus } from '../api/orders';
import { formatUtcDate, formatUtcDateTime } from '../lib/format';
import { SectionTitle } from '../staff/ui';
import { Badge, Card, ErrorState, Help, TextInput } from '../ui/kit';
import { CONTROL_ROW_HEIGHT, FilterChip, FilterChipRow, FiltersTrigger } from '../ui/filters';
import {
  activePatientFilterCount,
  EMPTY_PATIENT_FILTERS,
  PatientsFilters,
  TEST_RECORDS_CHIP,
  type PatientsFilterState,
} from './PatientsFilters';
import { Pagination, PAGE_SIZE } from '../ui/Pagination';
import { DataTable, Td, Th } from '../ui/Table';
import { TableSkeleton } from '../ui/skeletons';
import { PatientRecordDrawer, type PatientRecordSummary } from './PatientRecordDrawer';

export type PmsVariant = 'admin' | 'clinician';

/** How long a search keystroke settles before the server-side `q` fires. */
const SEARCH_DEBOUNCE_MS = 300;

/** Human wording for the plan states an ADMIN may see. */
const PLAN_STATE_LABEL: Record<string, string> = {
  dormant: 'never started',
  active: 'active',
  past_due: 'payment failed',
  cancelled: 'cancelled',
  completed: 'completed',
};

/**
 * The treatment cell.
 *
 * `undefined` means we have not been told — either the read is still in flight
 * or it failed — and renders exactly like "no plan": an em dash. That is the
 * deliberate fail-soft (see the fetch effect). A `Badge` only appears when we
 * have a real answer, so the column never asserts something it does not know.
 *
 * The plan state is shown as a QUALIFIER in words beside the badge, never as
 * the badge itself: "in treatment" is the clinically load-bearing fact and must
 * not be crowded out by a billing word. A clinician's response carries no plan
 * state, so they simply see the badge.
 */
function TreatmentCell({ status }: { status?: import('../api/types').PatientTreatmentStatus }) {
  if (!status) return <>—</>;
  const plan = status.planState ? PLAN_STATE_LABEL[status.planState] ?? status.planState : null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <Badge tone={status.inTreatment ? 'positive' : 'neutral'}>
        {status.inTreatment ? 'In treatment' : 'Not in treatment'}
      </Badge>
      {plan ? <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>{plan}</span> : null}
    </span>
  );
}

/**
 * The one name for a patient on this screen: their stored name, or their email
 * when none is stored (the identity the contacts table actually keys on). Named
 * Named once so the row headline and anything that later names a patient agree
 * on which of the two fields to show.
 */
function patientLabel(row: AdminPatientRow): string {
  return row.fullName?.trim() || row.email;
}

export function PatientsTab({ variant }: { variant: PmsVariant }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const patientParam = searchParams.get('patient');
  /**
   * THE APPLIED FILTERS (2026-09-05) — today exactly one facet, "Show test
   * records", the opt-in audit view (identity #86).
   *
   * It is a filter STATE object rather than a bare boolean because the screen
   * now wears the board's filter grammar (see `PatientsFilters.tsx`): the
   * "Filters (n)" button counts it, a chip shows it, and the next facet is one
   * more key here rather than a second parallel piece of state.
   *
   * DEFAULT OFF, and STATE rather than a URL param on purpose: the directory's
   * one deep link is `?patient=`, which is a link staff PASTE to each other.
   * Folding a reporting-hygiene toggle into that URL would mean a pasted
   * patient link could silently open a colleague's directory in the audit view
   * — the same class of accident the literal-string `includeTest` enum exists
   * to prevent one layer down.
   */
  const [filters, setFilters] = useState<PatientsFilterState>(EMPTY_PATIENT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const showTest = filters.showTest;
  const appliedCount = activePatientFilterCount(filters);

  // The search box value debounces into `query` — the value actually sent as
  // `q` (trimmed; blank = omitted entirely, the strict boundary 400s an empty q).
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Cursor-page cache for the CURRENT query: pages[i] = the rows of page i+1.
  // A query change resets everything (new search = new cursor chain).
  const [pages, setPages] = useState<AdminPatientRow[][]>([]);
  const [page, setPage] = useState(1);
  const [endReached, setEndReached] = useState(false);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // Stable fetcher — everything it needs arrives as arguments, so the query
  // effect and the page-change handler share it without stale-closure risk.
  const fetchPage = useCallback(
    (targetPage: number, existing: AdminPatientRow[][], q: string, includeTest: boolean) => {
    // `before` = the previous page's last createdAt (the strictly-older cursor).
    const prior = existing[targetPage - 2];
    const before = targetPage > 1 ? prior?.[prior.length - 1]?.createdAt : undefined;
    setStatus('loading');
    // ⚠️ `'true'` — the STRING. identity's query schema is an enum, not a
    // coerced boolean, and the param is omitted entirely when the toggle is
    // off: `'false'` behaves identically to absence server-side, so sending it
    // would only add a second wire shape for "the normal read".
    listPatients({
      ...(q ? { q } : {}),
      ...(before ? { before } : {}),
      ...(includeTest ? { includeTest: 'true' as const } : {}),
    })
      .then(({ items }) => {
        if (items.length === 0 && targetPage > 1) {
          // The previous page was exactly full and this one is empty — the
          // end was one click behind us; stay on the last real page.
          setEndReached(true);
          setPage(targetPage - 1);
          setStatus('ready');
          return;
        }
        setPages((prev) => {
          const next = [...prev];
          next[targetPage - 1] = items;
          return next;
        });
        // A short page IS the end — no further cursor fetch will find rows.
        if (items.length < PAGE_SIZE) setEndReached(true);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
    },
    [],
  );

  // A query change (first load included) resets the cursor chain and fetches
  // page 1 fresh. `showTest` is a dependency for the same reason `query` is:
  // it changes what the SERVER returns, so the whole cursor chain it produced
  // is the wrong chain — page two of "patients" is not page two of "patients
  // including rehearsals".
  useEffect(() => {
    setPages([]);
    setPage(1);
    setEndReached(false);
    fetchPage(1, [], query, showTest);
  }, [query, showTest, fetchPage]);

  // Previous re-shows the cached page instantly; Next onto an unseen page
  // fetches it through the cursor.
  function goToPage(target: number) {
    setPage(target);
    if (pages[target - 1] === undefined) fetchPage(target, pages, query, showTest);
  }

  const rows = pages[page - 1];

  /**
   * The treatment column, for the VISIBLE page only (orders #186).
   *
   * ⚠️ FAIL-SOFT AND NON-BLOCKING, deliberately. This is a supplementary column
   * on a directory whose real job is finding people: an orders outage must not
   * empty the table or put an error where a name belongs. A failed read leaves
   * the map empty and the cells show an em dash — the same as a patient who
   * genuinely has no plan. Distinguishing "no plan" from "we could not ask" is
   * not worth a second visual state here, because nobody acts differently on it.
   *
   * ⚠️ THE ROLE SPLIT IS THE SERVER'S. Both consoles make this call; orders
   * decides from the gateway-verified role whether `planState` is in the
   * response at all, and a CLINICIAN shape has no such key. So there is
   * deliberately no `variant` check around the fetch — adding one would look
   * like the control while the real one lives server-side (R41).
   *
   * Keyed on the row ids, so paging re-asks and a re-render does not.
   */
  const rowIds = (rows ?? []).map((r) => r.userId).join(',');
  const [treatment, setTreatment] = useState<Map<string, PatientTreatmentStatus>>(new Map());
  useEffect(() => {
    const ids = rowIds ? rowIds.split(',') : [];
    if (ids.length === 0) {
      setTreatment(new Map());
      return;
    }
    let alive = true;
    getPatientTreatmentStatus(ids)
      .then(({ items }) => {
        if (alive) setTreatment(new Map(items.map((i) => [i.userId, i])));
      })
      .catch(() => {
        if (alive) setTreatment(new Map());
      });
    return () => {
      alive = false;
    };
  }, [rowIds]);
  const knownCount = useMemo(() => pages.reduce((sum, p) => sum + (p?.length ?? 0), 0), [pages]);
  // The cursor read never knows the true total: advertise one MORE page while
  // the end hasn't been seen, so Next stays enabled exactly when it can work.
  const paginationTotal = knownCount + (endReached ? 0 : PAGE_SIZE);

  const loaded = useMemo(() => pages.flat(), [pages]);
  const selectedRow = patientParam ? loaded.find((r) => r.userId === patientParam) ?? null : null;

  // Deep-link fallback: the id isn't on any loaded page — resolve name/email
  // via the batched lookup (ADMIN-only route; the clinician variant renders
  // the unresolved record instead of firing a request the gateway 403s).
  const [fallback, setFallback] = useState<{ userId: string; email: string | null; fullName: string | null } | null>(null);
  useEffect(() => {
    if (!patientParam || selectedRow || variant !== 'admin') return;
    let cancelled = false;
    lookupStaffContactMap([patientParam])
      .then((map) => {
        if (cancelled) return;
        const contact = map[patientParam];
        setFallback({ userId: patientParam, email: contact?.email ?? null, fullName: contact?.fullName ?? null });
      })
      .catch(() => {
        if (cancelled) return;
        /**
         * ⚠️ A FAILED LOOKUP IS STILL A SETTLED ANSWER, AND IT HAS TO BE
         * RECORDED AS ONE. Best-effort for the CONTACT, exactly as before —
         * an empty resolution renders the drawer with the raw id. But this
         * lane now answers a SECOND question for `TestRecordAction`: "is
         * anything still coming that could tell us the test flag?". Leaving
         * the state untouched here answers that "yes, forever", which
         * suppresses the unreadable-flag statement on precisely the route it
         * exists for. "Can we read the flag" is NO whether the lookup came
         * back empty or did not come back at all, so we settle it either way.
         */
        setFallback({ userId: patientParam, email: null, fullName: null });
      });
    return () => {
      cancelled = true;
    };
  }, [patientParam, selectedRow, variant]);

  const selected: PatientRecordSummary | null = useMemo(() => {
    if (!patientParam) return null;
    if (selectedRow) {
      return {
        userId: selectedRow.userId,
        email: selectedRow.email,
        fullName: selectedRow.fullName,
        emailVerified: selectedRow.emailVerified,
        createdAt: selectedRow.createdAt,
        lastLoginAt: selectedRow.lastLoginAt,
        isTest: selectedRow.isTest,
        // The row IS the read, and it carries the flag — there is nothing
        // outstanding that could change the answer.
        testFlagPending: false,
      };
    }
    const resolved = fallback && fallback.userId === patientParam ? fallback : null;
    return {
      userId: patientParam,
      email: resolved?.email ?? null,
      fullName: resolved?.fullName ?? null,
      emailVerified: null,
      createdAt: null,
      lastLoginAt: null,
      // ⚠️ NULL, NOT FALSE — AND `null` IS NOT A QUIETER `false`. The deep-link
      // fallback resolves a name and an email and nothing else: the contact
      // lookup (`POST /v1/identity/admin/users/lookup`) carries no flag, and
      // there is no single-patient directory read that does, so on this route
      // we genuinely do not know.
      //
      // ⚠️ WHAT `null` DOES **NOT** BUY US (this comment used to claim it did,
      // and that claim went stale at 41d6acc when the MARK was deleted). The
      // drawer's `flagged` is `isTest === true`, so `null` and `false` hide the
      // un-mark IDENTICALLY. Keeping `null` therefore does not preserve the
      // un-mark on this route — nothing here can, because the un-mark's label
      // asserts the patient IS flagged and we have not read that.
      //
      // WHAT IT DOES BUY, AND WHY IT STAYS: `false` would ASSERT something we
      // never read. It would tell the drawer "this patient is not a test
      // record", which is a lie about a flagged patient — and, worse, an
      // invisible one, because an unflagged patient's Test record section is
      // (correctly) absent, so the screen would look right while being wrong.
      // `null` is the honest value, and it is the value `TestRecordAction`
      // keys its statement off: on `null` it renders "opened by direct link,
      // status not read, use Patients → Show test records" in place of the
      // control it cannot offer. Flip this to `false` and that statement
      // disappears with no other visible change — the screen silently goes
      // back to lying by omission.
      //
      // The real fix is a SERVER read that carries the flag for one id; it is
      // written up on `TestRecordAction` in PatientRecordDrawer.tsx.
      isTest: null,
      /**
       * ⚠️ AND THE DIFFERENCE BETWEEN "COULD NOT READ IT" AND "HAVE NOT READ
       * IT YET" (2026-09-05 defect). `isTest` is `null` here from the FIRST
       * render — `selectedRow` cannot exist until `listPatients()` resolves —
       * so the drawer, keying its statement off `null` alone, told operators
       * the flag "could not be read" during every healthy load, on ordinary
       * unflagged patients who were about to appear on page 1. It then
       * vanished. The claim was false for as long as it was on screen.
       *
       * THIS COMPONENT IS THE ONE THAT KNOWS, which is why the flag is
       * computed here and not derived in the drawer: only this component
       * holds both reads that could still answer the question.
       *  - `status === 'loading'` — the directory page read is in flight. It
       *    may yet produce the row, flag and all.
       *  - `resolved === null` — the deep-link contact lookup has not settled
       *    for THIS id. It never carries the flag, but until it settles the
       *    record is not resolved by any route and the screen has nothing to
       *    be honest about yet.
       * Either outstanding → say nothing. Both settled → the flag is
       * genuinely unreadable, and the drawer states so and names the route
       * that works.
       *
       * `status === 'error'` is SETTLED, deliberately: a failed directory read
       * will not produce the row either, and that is the case where an
       * operator most needs to be told the flag is unknown rather than shown
       * a confident blank.
       *
       * The CLINICIAN variant never fires the lookup (it would 403), so this
       * stays `true` for them and the statement never appears — which is the
       * decision anyway: `TestRecordAction` sits behind `canReset`.
       */
      testFlagPending: status === 'loading' || resolved === null,
    };
  }, [patientParam, selectedRow, fallback, status]);

  // The open record lives in the URL — open/close rewrite `?patient=` (replace,
  // preserving `?tab=`/`?clinic=`) so every record is a copyable deep link.
  function openPatient(userId: string) {
    const next = new URLSearchParams(searchParams);
    next.set('patient', userId);
    setSearchParams(next, { replace: true });
  }

  function closePatient() {
    const next = new URLSearchParams(searchParams);
    next.delete('patient');
    setSearchParams(next, { replace: true });
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <Card>
          {/* ⚠️ ONE TOOLBAR ROW: TITLE, SEARCH, FILTERS, CHIPS — IN THAT ORDER
              (2026-09-05, CTO: on Patients "the Filters control sits before the
              search box; it should come after it, matching the pipeline board").

              It did, visually. The search box was pinned to the RIGHT end of the
              title row and the Filters button sat at the LEFT end of a second row
              underneath, so the eye met Filters first and then Search — the
              reverse of the board, where the search input leads and the button
              follows it along the same line. DOM order alone was never the
              defect: the two controls were on different lines, which is also why
              the 2026-08-31 instruction ("keep both filters and search in the
              same line") was only half kept here.

              They are one row now, in the board's order, drawing on the board's
              own components (`ui/filters.tsx`). The title keeps its place at the
              head of the row rather than moving back above it: the 2026-08-31
              pass put the search on the title row precisely so the eye did not
              meet a bare input, a card border and then the heading it belongs to
              — three bands of chrome before the first patient — and splitting
              them again to make room for the button would buy the ordering back
              at that cost.

              `data-testid` because "same row, this order" is the invariant, and
              document order alone cannot express it — it was already satisfied
              by the layout this replaces.

              `alignItems: 'flex-start'` for the reason the pipeline bar gives:
              the chip block is the one item allowed to grow taller than the
              48px control height, and centring against it would drag the search
              input down the bar as filters are applied.

              `flexWrap` so a narrow console stacks these rather than crushing
              the input — the search is the one control here that must stay
              usable at any width. */}
          <div
            data-testid="patients-toolbar"
            style={{
              display: 'flex',
              gap: 'var(--gps-s-3)',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            {/* The heading owns the start of the row; the box (not the
                h-element) carries the control height so the title's text lands
                on the controls' centre line without touching SectionTitle's own
                type. */}
            <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', minHeight: CONTROL_ROW_HEIGHT }}>
              <SectionTitle>Patients</SectionTitle>
            </div>
            {/* ⚠️ NOTHING GOES IN THIS COLUMN THAT CHANGES ITS HEIGHT — the same
                rule the pipeline bar records. It holds one 48px input; a hint, a
                caveat or a visible label here makes the column taller than the
                controls beside it and no alignment property fixes that. The
                label is screen-reader-only for exactly this reason. */}
            <div style={{ flex: '0 1 24rem', minWidth: '15rem' }}>
              <label htmlFor="pms-patient-search" className="gps-sr-only">
                Search patients
              </label>
              <TextInput
                id="pms-patient-search"
                type="search"
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ height: CONTROL_ROW_HEIGHT }}
              />
            </div>
            {/* THE FILTER TOOLBAR — the board's grammar, not a second one. This
                was a bare inline checkbox while the CRM board offered the same
                idea, in the same words, behind a "Filters (n)" button with a
                modal and a removable chip. It is now the shared `FiltersTrigger`
                + `PatientsFilters` + `FilterChip` the pipeline's own bar draws
                from, so the two cannot drift apart again. */}
            <FiltersTrigger
              appliedCount={appliedCount}
              onOpen={() => setFiltersOpen(true)}
              onClearAll={() => setFilters(EMPTY_PATIENT_FILTERS)}
            />
            {/* ⚠️ THE CHIPS SIT IN THIS ROW, BESIDE THE BUTTON — never on their
                own row above the table. A full-width chip row appears the instant
                a filter is applied and pushes the table down by its own height,
                so the rows the admin is filtering jump away from them at the
                moment they act, and jump back when the last chip goes. The bar
                has empty space to the right of the button; the chips take it,
                and nothing below moves. The sizing is `FilterChipRow`'s, shared
                with the board — see ui/filters.tsx. */}
            {appliedCount > 0 ? (
              <FilterChipRow>
                {filters.showTest ? (
                  <FilterChip
                    label={TEST_RECORDS_CHIP}
                    onRemove={() => setFilters((f) => ({ ...f, showTest: false }))}
                  />
                ) : null}
              </FilterChipRow>
            ) : null}
          </div>

          {/* ⚠️ THE HEADER ROW IS OUTSIDE THIS BRANCH, DELIBERATELY.
              The search used to sit above the Card, so it survived every state.
              Moving it onto the title row put it inside the success branch —
              which meant an ERRORING search removed the only control that could
              change the term, leaving a Retry that re-fires the SAME failing
              query. A dead end built out of a layout tweak. The Card and its
              header now render in all three states and only the BODY switches. */}
          {status === 'error' ? (
            <ErrorState message="Sorry — we could not load the patients." onRetry={() => fetchPage(page, pages, query, showTest)} />
          ) : rows === undefined ? (
            // Wave 6 §1: row-shaped loading — the directory's columns as rows.
            <TableSkeleton rows={5} columns={7} label="Loading patients…" />
          ) : (rows ?? []).length === 0 ? (
            <Help style={{ marginBottom: 0 }}>
              {/* ⚠️ NO "no test records" BRANCH HERE, and that is deliberate.
                  `includeTest=true` WIDENS the list — it adds the flagged rows
                  to the real ones, it does not filter down to them — so an
                  empty page in that view means the directory is empty, not that
                  nothing is marked. A "no test records have been marked" line
                  would be a claim this read cannot support. */}
              {query ? 'No patients match this search.' : 'No patients yet — accounts appear as people register.'}
            </Help>
          ) : (
            <>
              <DataTable
                label="Patients"
                head={
                  <>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Age</Th>
                    <Th>Treatment</Th>
                    <Th>Verified</Th>
                    <Th>Created</Th>
                    <Th>Last login</Th>
                  </>
                }
              >
                {(rows ?? []).map((patient) => {
                  const open = () => openPatient(patient.userId);
                  return (
                    <tr key={patient.userId} onClick={open} style={{ cursor: 'pointer' }}>
                      <Td style={{ verticalAlign: 'middle' }}>
                        {/* Name headline (email fallback when none is stored) —
                            the row's one real control, so keyboard users get it. */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            open();
                          }}
                          style={{
                            minHeight: '44px',
                            padding: '0 4px',
                            background: 'none',
                            border: 0,
                            color: 'var(--gps-link)',
                            font: 'inherit',
                            fontSize: '14px',
                            fontWeight: 600,
                            textDecoration: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {/* STRUCK THROUGH ON A FLAGGED ROW — the same
                              treatment the pipeline card gives a marked deal
                              (PipelineBoard.tsx). The strikethrough is on the
                              NAME because the name is what a member of staff
                              scans; it is decoration on top of the chip beside
                              it, never the only cue (WCAG 1.4.1). */}
                          <span style={patient.isTest ? { textDecoration: 'line-through' } : undefined}>
                            {patientLabel(patient)}
                          </span>
                        </button>
                        {/* The MUTED chip, verbatim from the pipeline card. Muted
                            is the point: staff must be able to tell a rehearsal
                            from a real patient at a glance without it shouting
                            over the clinical cues in the row. On the DEFAULT
                            read this can never render — the server excludes
                            flagged rows before `take` — so its only home is the
                            "Show test records" view. */}
                        {patient.isTest ? (
                          <span style={{ marginLeft: '6px' }}>
                            <Badge tone="muted">Test</Badge>
                          </span>
                        ) : null}
                      </Td>
                      <Td style={{ verticalAlign: 'middle', overflowWrap: 'anywhere' }}>{patient.email}</Td>
                      {/* AGE, not date of birth (identity #82). An integer is
                          what the column needs, and DOB appears on no other
                          staff surface — a full date here would be new exposure
                          of a stronger identifier. A dash for unknown, never 0,
                          which would read as a newborn. */}
                      <Td style={{ verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums' }}>
                        {patient.age ?? '—'}
                      </Td>
                      {/* TREATMENT. `inTreatment` is a SUPPLY question, not a
                          billing one (orders #186) — a plan can be billing-stopped
                          while the patient still holds medication, which is why
                          this is not derived from the plan state. ADMINs also see
                          that state; a CLINICIAN response has no such key at all,
                          decided server-side. */}
                      <Td style={{ verticalAlign: 'middle' }}>
                        <TreatmentCell status={treatment.get(patient.userId)} />
                      </Td>
                      <Td style={{ verticalAlign: 'middle' }}>
                        <Badge tone={patient.emailVerified ? 'positive' : 'pending'}>
                          {patient.emailVerified ? 'Verified' : 'Unverified'}
                        </Badge>
                      </Td>
                      <Td style={{ verticalAlign: 'middle' }}>{formatUtcDate(patient.createdAt)}</Td>
                      <Td style={{ verticalAlign: 'middle' }}>{formatUtcDateTime(patient.lastLoginAt) || '—'}</Td>
                    </tr>
                  );
                })}
              </DataTable>
              <div style={{ marginTop: 'var(--gps-s-3)' }}>
                <Pagination label="Patients pages" page={page} total={paginationTotal} onPageChange={goToPage} />
              </div>
            </>
          )}
      </Card>

      {selected ? (
        <PatientRecordDrawer
          key={selected.userId}
          patient={selected}
          variant={variant}
          onClose={closePatient}
          /**
           * ⚠️ PATCHED IN PLACE FROM THE SERVER'S ANSWER, AND THE ROW IS NOT
           * DROPPED.
           *
           * A refetch would be the obvious move and it is the wrong one: the
           * pages are a CURSOR chain, so re-reading the page the admin is
           * standing on leaves every EARLIER cached page still showing the row
           * as it was, and resetting the chain to page one throws away their
           * place on a screen whose whole job is finding one person.
           *
           * Dropping the newly-flagged row from the default view was the
           * previous behaviour and is deliberately NOT restored: the write now
           * happens in the drawer, which is open OVER the table, so nobody is
           * looking at the row — and if it vanished, `selectedRow` would go
           * with it and the open drawer would lose the very flag it just set,
           * hiding the un-mark behind the mark. The row keeps its place,
           * wearing the chip that proves the write landed, and the next read of
           * that page drops it the way the server always would.
           */
          onTestFlagChange={(userId, isTest) =>
            setPages((prev) =>
              prev.map((pageRows) =>
                pageRows === undefined
                  ? pageRows
                  : pageRows.map((r) => (r.userId === userId ? { ...r, isTest } : r)),
              ),
            )
          }
        />
      ) : null}

      {/* Mounted only while open — `DialogShell` has no `open` prop: mounting
          IS opening, which is what gives it focus-in on mount and focus-return
          to the control that opened it. */}
      {filtersOpen ? (
        <PatientsFilters
          applied={filters}
          onApply={(next) => {
            setFilters(next);
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
        />
      ) : null}
    </div>
  );
}
